import { useEffect, useRef, useState } from 'react';
import { FormikHelpers } from 'formik';
import { AxiosRequestConfig } from 'axios';
import { callApi } from 'src/core/api/callApi';
import { createOrUpdateCustomer, updateCustomerAddress, getAllCustomers, getCustomerById } from 'src/core/api/apiRequests';
import { clearCacheForUrl } from 'src/core/api/cache/idbUtility';
import { useMessageContext } from 'src/shared/support/MessageContext';

export const useCustomerSubmit = (initialValues: any) => {
  const [result, setResult] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const activeRef = useRef(false);
  const mountedRef = useRef(true);
  const cancelRef = useRef<() => void>();
  const baselineRef = useRef(initialValues);
  useEffect(() => { baselineRef.current = initialValues; }, [initialValues]);
  const { updateMessageStatus } = useMessageContext();
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; cancelRef.current?.(); }; }, []);

  const submit = async (values: any, helpers: FormikHelpers<any>) => {
    if (activeRef.current) return;
    activeRef.current = true;
    setSubmitting(true);
    setResult(null);
    try {
      const requests = buildCustomerRequests(values, baselineRef.current);
      if (!requests.length) {
        updateMessageStatus({ requestUrl: getCustomerById(String(values.AccountId)).url, status: 'success', message: 'No changes to save.' });
        return;
      }
      let response: any = null;
      for (const request of requests) {
        response = await new Promise((resolve, reject) => {
          cancelRef.current = callApi({ request, config: { isMountedRef: () => mountedRef.current, updateMessageStatus, logLevel: ['error'], resolve, reject } });
        });
        if (!mountedRef.current) return;
      }
      if (requests.length) {
        await clearCacheForUrl(getAllCustomers(1000, 0).url);
        if (values.AccountId) await clearCacheForUrl(getCustomerById(String(values.AccountId)).url);
        setResult(response ?? 'No Content');
        baselineRef.current = values;
        helpers.resetForm({ values });
        updateMessageStatus({ requestUrl: requests[0].url, status: 'success', message: 'Customer saved.' });
      }
    } catch {
      // Transport has reported the failing request. Keep the complete draft for correction/retry.
    } finally {
      activeRef.current = false;
      if (mountedRef.current) {
        setSubmitting(false);
        helpers.setSubmitting(false);
      }
    }
  };
  return [result, submit, submitting] as const;
};

export const buildCustomerRequests = (values: any, initialValues: any = {}): AxiosRequestConfig[] => {
  const accountId = values.AccountId;
  const data = customerHeader(values);
  if (!accountId) return [createOrUpdateCustomer({ ...data, LegalAddress: customerAddress(values, 'LegalAddress') })];
  const initialHeader = customerHeader(initialValues);
  const changedHeader = Object.fromEntries(Object.entries(data).filter(([key, value]) => !createOnlyFields.has(key) && JSON.stringify(value) !== JSON.stringify(initialHeader[key])) );
  const requests: AxiosRequestConfig[] = Object.keys(changedHeader).length ? [createOrUpdateCustomer(changedHeader, accountId)] : [];
  const addressIds = new Set<string>();
  for (const prefix of ['LegalAddress', 'BillingAddress']) {
    const addressId = values[prefix];
    if (!addressId || addressIds.has(String(addressId))) continue;
    addressIds.add(String(addressId));
    const address = customerAddress(values, prefix);
    const initialAddress = customerAddress(initialValues, prefix);
    const changedAddress = Object.fromEntries(Object.entries(address).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(initialAddress[key])));
    if (addressId && Object.keys(changedAddress).length) requests.push(updateCustomerAddress(changedAddress, accountId, addressId));
  }
  return requests;
};

const customerHeader = (values: any) => {
  const corporate = Number(values.CustomerTypeId) === 2;
  const fields = corporate ? ['CompanyName', ...headerFields.filter(name => name !== 'FirstName' && name !== 'LastName')] : headerFields;
  return Object.fromEntries(fields.map(name => [name, numberFields.has(name) ? nullableNumber(values[name]) : booleanFields.has(name) ? values[name] === true || values[name] === 'true' : values[name] ?? null]));
};
const customerAddress = (values: any, prefix: string) => ({
  ...Object.fromEntries(addressFields.map((name) => [name, values[`${prefix}_${name}`] ?? null])),
  Country: { Key: nullableNumber(values[`${prefix}_Country`]), Value: values[`${prefix}_Country_Value`] ?? null }
});
const nullableNumber = (value: any) => (value === '' || value == null ? null : Number(value));
const headerFields = ['FirstName', 'LastName', 'Description', 'Synchronize', 'CustomerTypeId', 'CustomerCategoryId', 'DiscountCategoryId', 'SalesPriceListId', 'SalesPriceListHasTaxIncluded', 'OrganisationId', 'CorporateRegistrationNumber', 'VATNumber', 'CreditLimit', 'OrderDefaultCurrencyId', 'OrderDefaultShippingMethodId', 'OrderDefaultShippingTermsId', 'OrderDefaultShippingTermsDestination', 'OrderDefaultPaymentMethodId', 'OrderDefaultPaymentTermsId', 'OrderDefaultInvoiceCreationModeId', 'SalesChannelId'];
const numberFields = new Set(['CustomerTypeId', 'CustomerCategoryId', 'DiscountCategoryId', 'SalesPriceListId', 'OrganisationId', 'CreditLimit', 'OrderDefaultCurrencyId', 'OrderDefaultShippingMethodId', 'OrderDefaultShippingTermsId', 'OrderDefaultPaymentMethodId', 'OrderDefaultPaymentTermsId', 'OrderDefaultInvoiceCreationModeId', 'SalesChannelId']);
const booleanFields = new Set(['Synchronize', 'SalesPriceListHasTaxIncluded']);
const addressFields = ['Title', 'Salutation', 'FirstName', 'MiddleNameInitial', 'LastName', 'Street', 'StreetAdditionalInformation', 'Postcode', 'City', 'County', 'State', 'AdditionalInformation', 'DoorCode', 'Contact', 'EmailAddress', 'PhoneNumber', 'MobileNumber', 'FaxNumber', 'WebAddress'];

const createOnlyFields = new Set(['OrganisationId', 'SalesPriceListHasTaxIncluded']);
